import React, { useEffect } from 'react';
import { HashRouter, BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next'; // SPRINT 9: Ensure i18n provider for all components
import i18n from './i18n/i18n'; // SPRINT 9: Import i18n instance
import { HelmetProvider } from 'react-helmet-async'; // PUBLIC-SEO-LAUNCH-GAP-REMEDIATION: shared metadata authority
import { SurfaceProvider } from './context/SurfaceContext'; // TREATX-L1F: canonical surface resolver
import { isPublicPath, isPrivatePath, resolveSurface, setActiveSurface, getActiveSurface } from './routing/publicSurface'; // TREATX-SEO-RECOVERY-S2
import NotFoundPage from './pages/NotFoundPage'; // TREATX-SEO-RECOVERY-S3A
import { AuthProvider, useAuth } from './context/AuthContext';
import { AgentAuthProvider, useAgentAuth } from './context/AgentAuthContext';
import { CoordinatorAuthProvider, useCoordinatorAuth } from './context/CoordinatorAuthContext';
import { RBACProvider, useRBAC } from './context/RBACContext';
import { getActiveWorkspaceRole, getActiveWorkspaceClinicId } from './utils/activeWorkspace';
import WorkspaceBootstrapGuard from './components/WorkspaceBootstrapGuard';
import WorkspaceRouteSync from './components/WorkspaceRouteSync';
// CONSENT-INTERCEPTOR-REMEDIATION-01 — `ReconsentGate` is no longer mounted
// (permanently inert + second re-consent authority). Import removed; the file
// is retained untouched for history.
import VersionBadge from './components/VersionBadge';
import { initRuntimeObservability } from './runtime/runtimeObservability';
import SEO from './components/SEO';

// COM-FOUNDATION — Initialise the runtime observability handle once
// at module load.  Mounts `window.__TREATX_RUNTIME__`, starts the
// parity poller, installs history-event observers, and computes the
// served-bundle hash.  Idempotent.
initRuntimeObservability();
import { CoordinatorDataProvider } from './context/CoordinatorDataContext';
import { ClinicRepresentativeAuthProvider } from './context/ClinicRepresentativeAuthContext';
import { ClinicRepresentativeDataProvider } from './context/ClinicRepresentativeDataContext';
import { TreatXAdminAuthProvider } from './context/TreatXAdminAuthContext';
import { TreatmentManagementProvider } from './context/TreatmentManagementContext';
import { CategoryManagementProvider } from './context/CategoryManagementContext';

import { ProviderOnboardingProvider } from './context/ProviderOnboardingContext';
import { OrganizationRolesProvider } from './context/OrganizationRolesContext';
import { BoardCertificationProvider } from './context/BoardCertificationContext';
import { PackageEnforcementProvider } from './context/PackageEnforcementContext';
import { SuspensionEnforcementProvider } from './context/SuspensionEnforcementContext';
import { PackageCatalogProvider } from './context/PackageCatalogContext'; // Sprint 10: Dynamic package catalog
import { RatingsProvider } from './context/RatingsContext';
import { PaymentProvider } from './context/PaymentContext';
import { LanguageProvider } from './context/LanguageContext';
import { PatientFlowProvider } from './context/PatientFlowContext';
import { ClinicDataProvider, useClinicData } from './context/ClinicDataContext';
import { MonetizationProvider } from './context/MonetizationContext';
import { DocumentProvider } from './context/DocumentContext';
// HYGIENE-1: PatientJourney provider excised — legacy patient-journey ghost
// universe. Canonical operational workflow → milestone_instances +
// milestone_tasks (FE wiring deferred).
import { TravelProvider } from './context/TravelContext';
// R6-IMPL-002 (2026-02-28): `TimelineProvider` (sessionStorage-backed
// mock checklist) was unmounted from the runtime tree. It had zero
// live consumers — every reference outside its own definition file
// was a constitutional-reminder comment (R-11). The file
// `context/TimelineContext.js` is intentionally left on disk for one
// sprint as a safety net; it is no longer wired anywhere.
import { MessagingProvider } from './context/MessagingContext';
import { HandoffNotificationProvider } from './context/HandoffNotificationContext';
import { ContactInquiryProvider } from './context/ContactInquiryContext'; // Contact inquiries management
import PatientFlowProgress from './components/PatientFlowProgress';
import { Toaster } from './components/Toaster';
import { HandoffNotificationContainer, GlobalHandoffConfirmationModal } from './components/HandoffUI';
import Header from './components/Header';
import Footer from './components/Footer';
import SoftLaunchGate from './components/SoftLaunchGate';
import LandingPage from './pages/LandingPage';
import LandingPageV2 from './pages/landing-v2/LandingPageV2';
import LandingPageV3 from './pages/landing-v3/LandingPageV3';
import HomepageVersionGate from './pages/HomepageVersionGate';
import BuildInfo from './pages/BuildInfo';
// DEPLOY-TRUTH-R1 — owner-readable parity check surface.  Public, no
// auth, no feature flag; lives at /#/parity.
import ParityCheckPage from './pages/ParityCheckPage';
// PRD1.9 Phase 1: LoginPage and UnifiedLogin removed (legacy login surfaces)
import UnifiedLoginPage from './pages/UnifiedLoginPage'; // Sprint 1: Unified Identity
import MfaEnrollPage from './pages/MfaEnrollPage'; // TREATX-MFA-PROD-FIX-01
import MfaChallengePage from './pages/MfaChallengePage'; // TREATX-MFA-PROD-FIX-01
// TREATX-GOOGLE-OAUTH-FRONTEND-01: SocialAuthCallback (Emergent proxy) excised.
// Direct Google Identity Services replaces the redirect-based flow.
import ResetPassword from './pages/ResetPassword';
import PatientOnboarding from './pages/PatientOnboardingV2';
import PatientIntakeEnhanced from './pages/PatientIntakeEnhanced';
// TREATX-SEARCH-V1-FINAL-RETIRE-01 (2026-02): Search V1 (ClinicSearchPage)
// has been permanently retired. Every legacy entry point (`/search`,
// `/find-clinics`, `/patient/clinic-search`) flows through `SearchRedirect`
// → `/search-v2`. The V1 component and tests have been deleted.
import SearchV2Page from './pages/SearchV2Page';
import SavedSearchesPage from './pages/patient/SavedSearchesPage';
import ClinicRequestConfirmation from './pages/patient/ClinicRequestConfirmation';
import PatientDashboard from './pages/patient/PatientDashboard';
import PatientNotificationsCenter from './pages/patient/PatientNotificationsCenter';
import ClinicalProfileEdit from './pages/patient/ClinicalProfileEdit';
import MyOffers from './pages/patient/MyOffers';
import PaymentPage from './pages/patient/PaymentPage';
import PaymentSuccessPage from './pages/patient/PaymentSuccessPage';
import PaymentFailedPage from './pages/patient/PaymentFailedPage';
import PaymentHistory from './pages/patient/PaymentHistory';
import PatientDocuments from './pages/patient/PatientDocuments';
import BookingTimeline from './pages/patient/BookingTimeline';
import TravelDetails from './pages/patient/TravelDetails';
import PatientMessagesV2 from './pages/patient/PatientMessagesV2';
import MedicalFilesPage from './pages/patient/MedicalFilesPage';
import PatientJourney from './pages/patient/PatientJourney';
import ClinicSetup from './pages/ClinicSetup';
import ClinicDashboard from './pages/ClinicDashboard';
// PRD1.9 Phase 1: ClinicLogin removed (legacy login surface — use /login)
import ClinicAdminDashboard from './pages/clinic/ClinicAdminDashboard';
import ClinicOperationsDashboard from './pages/clinic/ClinicOperationsDashboard';
import SalesRepDashboard from './pages/clinic/SalesRepDashboard';
// PRD1.9 Phase 1: DoctorDashboard quarantined to /deprecated/
import PackageSelectionPage from './pages/clinic/PackageSelectionPage'; // Sprint 10: Clinic-facing upgrade page
// PRD1.9 SSOT: ClinicRegistrationPage & PendingApprovalPage removed — all provider onboarding goes through /provider/register (new domain)
import ApplicationStatusCheckPage from './pages/clinic/ApplicationStatusCheckPage'; // Phase 1: Status check
import RejectionStatusPage from './pages/clinic/RejectionStatusPage'; // Batch 1.5: Rejection & Reapply
import ClinicRepresentativeLogin from './pages/clinic/ClinicRepresentativeLogin';
import ClinicRepresentativeDashboard from './pages/clinic/ClinicRepresentativeDashboard';
import ClinicMessagesV2 from './pages/clinic/ClinicMessagesV2';
import ClinicQuoteRequests from './pages/clinic/ClinicQuoteRequests';
import UserManagement from './pages/clinic/admin/UserManagement';
// TREATX-PROVIDER-CMS-CONTRACTS-01 — canonical legal_documents UI for
// provider admins. Backed by GET/POST/PUT /api/documents and
// POST /api/documents/{id}/versions. Append-only, ADR-006 LOCKED.
import ContractsManagement from './pages/clinic/admin/ContractsManagement';
import PatientRegisterPage from './pages/PatientRegisterPage'; // Phase 1: Patient registration
import ForgotPasswordPage from './pages/ForgotPasswordPage'; // TREATX-AUTH-PASSWORD-RESET-SELFSERVE-01
// PRD1.9 Phase 1: CoordinatorLogin removed (legacy login surface — use /login)
// POST-CLEANUP: CoordinatorDashboard removed (dormant TreatX-side portal — use /clinic/operations for provider_coordinator).
// PRD1.9 Phase 1: TreatXAdminLogin removed (legacy login surface — use /login)
import TreatXAdminDashboard from './pages/treatx-admin/TreatXAdminDashboard';
import AfterSalesDashboard from './pages/clinic/AfterSalesDashboard';
import MilestoneTemplatesPage from './pages/clinic/admin/MilestoneTemplatesPage';
import ClinicCaseWorkflow from './pages/clinic/admin/ClinicCaseWorkflow';
// M16B-07D-B1 — canonical Coordinator Journey Planner (spreadsheet grid
// wired to patient_case_plans / materialize-plan / bulk-overrides).
import CoordinatorJourneyPlanner from './pages/clinic/CoordinatorJourneyPlanner';
// REQUIREMENT-FOUNDATION-001 — canonical Requirement Definition catalog
import RequirementCatalog from './pages/clinic/admin/RequirementCatalog';
// WORKFLOW-OPS-001-PHASE-1 — canonical operational case list shared by
// provider_admin (/clinic/admin) and provider_coordinator (/clinic/operations).
import ClinicCasesList from './pages/clinic/ClinicCasesList';
// WORKFLOW-OPS-001-PHASE-2A — canonical My Tasks page (all provider roles).
import MyTasksPage from './pages/clinic/MyTasksPage';
import BeforeAfterCasesPage from './pages/clinic/admin/BeforeAfterCasesPage';
import BeforeAfterModerationQueue from './pages/treatx-admin/BeforeAfterModerationQueue';
import SoftLaunchInviteKeysPage from './pages/treatx-admin/SoftLaunchInviteKeysPage';
import LaunchControlPage from './pages/treatx-admin/LaunchControlPage';
// PAYOUT-UI-01 · UI-A: legacy PayoutsPage.jsx deleted (normal withdrawal-request workflow retired). Any recovery/exception UI in UI-B/C composes canonical F6D authorities.
import EscrowPanel from './pages/treatx-admin/EscrowPanel'; // ESCROW-UI-01
import AdminFinance from './pages/treatx-admin/AdminFinance'; // UI-C
import CorporateIdentityAdmin from './pages/treatx-admin/CorporateIdentityAdmin'; // Issue 1
import VerifyEmailLandingPage from './pages/onboarding/VerifyEmailLandingPage';
import LogoModerationQueue from './pages/treatx-admin/LogoModerationQueue';
// OF-006 — canonical facility-media moderation queue (clinic_profile context).
import FacilityMediaModerationQueue from './pages/treatx-admin/FacilityMediaModerationQueue';
import ClinicLogoPage from './pages/clinic/admin/ClinicLogoPage';
import ClinicFacilityMediaPage from './pages/clinic/admin/ClinicFacilityMediaPage';
import BeforeAfterConsentPage from './pages/patient/BeforeAfterConsentPage';
import TreatmentManagement from './pages/treatx-admin/TreatmentManagement';
import CategoryManagement from './pages/treatx-admin/CategoryManagement';
import ProviderValidationPanel from './pages/treatx-admin/ProviderValidationPanel';
// PACKAGE-ADMIN-HYGIENE-1: `ClinicCRMPackageManagementPage` is now consumed
// only inside `PackagesAdminMerged` (the canonical product surface). Direct
// import here is no longer needed.
import AdminMessagingPanel from './pages/treatx-admin/AdminMessagingPanel'; // Messaging Oversight
import AdminAnalyticsDashboard from './pages/treatx-admin/AdminAnalyticsDashboard'; // Advanced Analytics P2
import ProviderOnboarding from './pages/provider/ProviderOnboarding';
import ProviderBenefits from './pages/provider/ProviderBenefits';
import ChangePassword from './pages/settings/ChangePassword';
import TreatmentsPage from './pages/TreatmentsPage';
import TreatmentDetailPage from './pages/TreatmentDetailPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import ReactivateAccountPage from './pages/ReactivateAccountPage';
import PaymentSuccess from './pages/PaymentSuccess';
import AppleCallback from './pages/AppleCallback';
import PaymentCancel from './pages/PaymentCancel';
import SystemStatus from './pages/SystemStatus';
// PRD1.9 Phase 1: AdminPanel removed (orphan — TreatX admin is at /treatx-admin)
import AgentLogin from './pages/agent/AgentLogin';
import AgentDashboard from './pages/agent/AgentDashboard';
import LeadDetail from './pages/agent/LeadDetail';
import QuoteBuilder from './pages/agent/QuoteBuilder';
import OrderDetail from './pages/agent/OrderDetail';
import PatientQuoteView from './pages/patient/PatientQuoteView';
import PatientOrderDetail from './pages/patient/PatientOrderDetail';
import PatientNotificationSettings from './pages/patient/PatientNotificationSettings'; // Phase N1.2
import OfferComparisonPage from './pages/patient/OfferComparisonPage';
import ClinicScorecardDashboard from './pages/clinic/ClinicScorecardDashboard';
import OfferTemplatesPage from './pages/clinic/OfferTemplatesPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import TreatXAdminInquiries from './pages/treatx-admin/TreatXAdminInquiries'; // Contact inquiries admin page
// TreatXAdminApplications legacy admin page removed — superseded by AdminProviderDomain (/treatx-admin/provider-domain)
import TreatXAdminEmailTemplates from './pages/treatx-admin/TreatXAdminEmailTemplates'; // P1: Email templates management
import CommercialGatingPolicy from './pages/treatx-admin/CommercialGatingPolicy'; // GATING-09B
import ProductLanguagesPolicy from './pages/treatx-admin/ProductLanguagesPolicy'; // PRODUCT-LANGUAGES-ADMIN-01
// TREATX-NOTIFICATION-ADMIN-OPS-11B
import NotificationOpsDashboard from './pages/treatx-admin/NotificationOpsDashboard';
import NotificationsLogViewer from './pages/treatx-admin/NotificationsLogViewer';
import WhatsAppAdmin from './pages/treatx-admin/WhatsAppAdmin';
import WhatsAppTest from './pages/treatx-admin/WhatsAppTest'; // TREATX-WA-DIAGNOSTIC-TEST-01 (temporary, super-admin-only)
import MaintenancePage from './pages/treatx-admin/MaintenancePage'; // TREATX-ADMIN-MAINTENANCE-01 (super-admin-only)
import ContentManagement from './pages/treatx-admin/ContentManagement'; // TREATX-MANAGED-RUNTIME-CONTENT-01 (super-admin-only)
import ContentProtectionOps from './pages/treatx-admin/ContentProtectionOps';
import AdminConsentDashboard from './pages/treatx-admin/AdminConsentDashboard';
import AdminAgreements from './pages/treatx-admin/AdminAgreements';
import { NewLegalDocumentPage, DocumentUsagePage, LegalPoliciesPage } from './pages/treatx-admin/LegalSelfServicePages';
import LegalMultilingualEditor from './pages/treatx-admin/LegalMultilingualEditor';
// USER-ROLE-MGMT-UI-01 · TreatX Admin — role management on existing canonical users
import UserRoleManagement from './pages/treatx-admin/UserRoleManagement';
import EmailTemplateOperations from './pages/treatx-admin/EmailTemplateOperations';
import PatientEmailTemplates from './pages/treatx-admin/PatientEmailTemplates'; // P1.2: Patient email templates
// TREATX-ADMIN-ONBOARDING-01 · frontend
import AdminOnboardingChangePassword from './pages/admin-onboarding/AdminOnboardingChangePassword';
import AdminOnboardingVerifyRequired from './pages/admin-onboarding/AdminOnboardingVerifyRequired';
import AdminOnboardingVerifyEmail from './pages/admin-onboarding/AdminOnboardingVerifyEmail';
// PostApprovalChoicePage removed — post-approval flow handled via /provider/register + unified login
import TrialActivationSuccessPage from './pages/clinic/TrialActivationSuccessPage'; // Phase 1 Batch 1.4: Trial success
import UpgradeSuccessPage from './pages/clinic/UpgradeSuccessPage'; // Phase 1 Batch 1.4: Upgrade success
import ClinicPaymentSuccess from './pages/clinic/ClinicPaymentSuccess'; // P3 Phase 3: Stripe payment success
import ClinicPaymentCancel from './pages/clinic/ClinicPaymentCancel'; // P3 Phase 3: Stripe payment cancel
import SubscriptionManagementPage from './pages/clinic/SubscriptionManagementPage'; // Subscription monetization UI
import ClinicAutoReleaseSettings from './pages/clinic/ClinicAutoReleaseSettings'; // Phase 5.1: Auto-release settings
import ClinicNotificationSettings from './pages/clinic/ClinicNotificationSettings'; // Phase N1.2: Notification prefs
import ClinicProfileEditor from './pages/clinic/ClinicProfileEditor'; // Phase 1.1: Profile editor
import ClinicTrustSubmission from './pages/clinic/ClinicTrustSubmission'; // PRD1.7 Step 4B: Trust submission
import ClinicCampaignsDashboard from './pages/clinic/ClinicCampaignsDashboard'; // Phase 1.1: Campaign management
import ClinicCampaignPerformance from './pages/clinic/ClinicCampaignPerformance'; // Campaign Performance Dashboard
import ClinicNotificationsCenter from './pages/clinic/ClinicNotificationsCenter'; // Campaign Alert Notifications
import AdminAutoReleasePage from './pages/treatx-admin/AdminAutoReleasePage'; // Phase 5.1: Auto-release admin
import AdminProfileReview from './pages/treatx-admin/AdminProfileReview'; // Phase 1.1: Profile review
import ClinicVerificationCenter from './pages/treatx-admin/ClinicVerificationCenter'; // Clinic Trust Layer
import AdminTrustScoreDashboard from './pages/treatx-admin/AdminTrustScoreDashboard'; // Trust Score Dashboard
import AdminPositioningTags from './pages/treatx-admin/AdminPositioningTags'; // Phase D1.1: Tag governance
import AdminVipServices from './pages/treatx-admin/AdminVipServices'; // Phase P6F: VIP services governance
import AdminLandingStats from './pages/treatx-admin/AdminLandingStats'; // Landing stats override
import AdminLandingCMS from './pages/treatx-admin/AdminLandingCMS'; // Landing CMS content blocks
import V3DraftReview from './pages/treatx-admin/V3DraftReview'; // authenticated V3 draft review

import AdminProviderBenefitsCMS from './pages/treatx-admin/AdminProviderBenefitsCMS'; // TREATX-ADMIN-CMS-HYGIENE-01: Provider Benefits page CMS editor
import AdminTranslationStatus from './pages/treatx-admin/AdminTranslationStatus'; // Translation completeness dashboard
import AdminDoctorVerification from './pages/admin/AdminDoctorVerification';
import AdminLandingSections from './pages/admin/AdminLandingSections'; // Landing V2 section management
import AdminHomepageVersion from './pages/treatx-admin/AdminHomepageVersion'; // Landing V3 Phase A — homepage version control
import WebsiteManagement from './pages/treatx-admin/WebsiteManagement'; // TREATX-WEBSITE-MGMT-01 — one entry point for the public website
import AdminSponsoredPlacements from './pages/admin/AdminSponsoredPlacements'; // Sponsored placements management
import AdminCampaignReview from './pages/admin/AdminCampaignReview'; // Campaign review & slot management
import AdminExposureRules from './pages/admin/AdminExposureRules'; // Exposure engine rules management
import AdminCommissionRules from './pages/treatx-admin/AdminCommissionRules'; // Commission rule management
import PackagesAdminMerged from './pages/treatx-admin/PackagesAdminMerged'; // PACKAGE-ADMIN-HYGIENE-1: single canonical package admin surface

// PACKAGE-ADMIN-HYGIENE-1 (2026-05-21): legacy PXC_B4_PACKAGES_IA_MERGE
// feature flag removed. `PackagesAdminMerged` is now the sole canonical
// product surface for package administration. The split-page rollback path
// is gone — the duplicate `PackageManagementPage` editor was excised.
// PROVIDER-FUNCTIONAL-CORRECTNESS-PACK (2026-02): Parallel `ClinicCampaignBuilder`
// removed. Canonical campaign surface is `ClinicCampaignsDashboard.jsx` at
// `/clinic/campaigns` (uses treatment_campaign_routes + clinic_treatment_campaigns).
import AdminTreatmentTaxonomy from './pages/treatx-admin/AdminTreatmentTaxonomy'; // Treatment Taxonomy CRUD
import AdminRankingPolicy from './pages/treatx-admin/AdminRankingPolicy'; // P5-5D: Ranking Policy Editor (frontend-only)
// AdminDocumentRules removed (owner-test defect fix sprint) — unified into AdminProviderDomain
import AdminProviderDomain from './pages/treatx-admin/AdminProviderDomain'; // Bounded Rebuild: unified provider management
import PublicClinicProfile from './pages/public/PublicClinicProfile'; // Phase 1.1: Public profile
import EnvBadge from './components/EnvBadge'; // OT-0F.1-D: collapsible env/build badge
import AccountingDashboard from './pages/accounting/AccountingDashboard';
import AccountantWorkspace from './pages/accounting/AccountantWorkspace';
import DoctorSignup from './pages/doctor/DoctorSignup';
import DoctorLogin from './pages/doctor/DoctorLogin';
import DoctorProfile from './pages/doctor/DoctorProfile';
import DoctorAffiliations from './pages/doctor/DoctorAffiliations';
import DoctorWorkspace from './pages/doctor/DoctorWorkspace';
import AfterSalesWorkspace from './pages/after-sales/AfterSalesWorkspace';
import CookieConsentBanner from './components/CookieConsentBanner';
import CookieSettingsPage from './pages/CookieSettingsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import HealthTourismInfoPage from './pages/HealthTourismInfoPage';
import OpenSourceLicensesPage from './pages/OpenSourceLicensesPage';
import PatientLegalSettings from './pages/patient/PatientLegalSettings';
// OF-001 — globally-mounted, self-hiding pending-invitations surface.
import PendingInvitationsBanner from './components/PendingInvitationsBanner';
import { GlobalConsentInterceptor } from './components/cms/GlobalConsentInterceptor';
import { PatientIdentityCompletionInterceptor } from './components/patient/PatientIdentityCompletionInterceptor';
import { initConsentEnforcement } from './services/CookieConsentService';
import './App.css';

// P1-001: Initialize consent enforcement before any tracking can load
initConsentEnforcement();


// Protected Route wrapper for patients/clinics
const ProtectedRoute = ({ children, requiredType }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredType && user.user_type !== requiredType) {
    return <Navigate to="/" replace />;
  }

  // CONSENT-INTERCEPTOR-REMEDIATION-01 — `ReconsentGate` was permanently
  // inert (`const token = null` → it always set `pending=[]` and rendered
  // children) and was a SECOND re-consent authority reading a different
  // endpoint. Ineffective, conflicting enforcement removed; the single
  // app-shell authority is <GlobalConsentInterceptor/>, backed by the
  // canonical server-side transaction guards.
  return children;
};

// Protected Route wrapper for agents
const AgentProtectedRoute = ({ children }) => {
  const { agent, loading } = useAgentAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!agent) {
    return <Navigate to="/agent/login" replace />;
  }

  return children;
};

// RBAC Phase 3: Unified permission-based route guard
// Replaces all legacy role-based guards (TreatXAdminProtectedRoute, AccountingProtectedRoute)
// OF-001E §C2 — route admission is WORKSPACE-EXISTENCE based.  If the
// user HAS a workspace whose role matches one of `allowedRoles`, the
// route is admissible regardless of which workspace is currently
// "active" in localStorage.  This eliminates the race where a click
// commits navigate(/clinic/sales) before the localStorage write
// propagates — under the old "active-role admission" model the route
// would bounce to /clinic/home and trigger the redirect loop that
// crashed React.  Workspaces[] is the deterministic SSOT for what
// the user CAN access; the active workspace is just which dashboard
// the user is CURRENTLY viewing.
// TREATX-UAT-PROD-ADMISSION-RACE-01 — neutral, non-committal bootstrap
// surface shown while the authoritative server state is being applied. It
// deliberately renders no protected content and no recovery/selector.
const AuthorizationBootstrap = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-slate-50"
    data-testid="authorization-bootstrap"
  >
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
      <p className="text-sm text-slate-500">Checking your access…</p>
    </div>
  </div>
);

const RBACProtectedRoute = ({ permission, allowedRoles, children }) => {
  const {
    isAuthenticated, workspaces, activeWorkspace, contextRequired, profileLoaded,
  } = useRBAC();

  const hasStoredPerms = () => {
    try {
      const storedPerms = localStorage.getItem('treatx_rbac_permissions');
      const storedRoles = localStorage.getItem('treatx_rbac_role_assignments');
      const perms = storedPerms ? JSON.parse(storedPerms) : [];
      const roles = storedRoles ? JSON.parse(storedRoles) : [];
      return perms.length > 0 || roles.length > 0;
    } catch {
      return false;
    }
  };

  // TREATX-PRODUCTION-P0-OWNER-ACCEPTANCE-02 §P0-7 (revised for FIND-H-08 cookie cutover):
  // The previous `authValid` check could bounce a freshly-loaded admin
  // page to /login if both `useRBAC().isAuthenticated` was still
  // hydrating AND `treatx_rbac_permissions` had not yet been written
  // (e.g. on a hard refresh in a tab where the perms cache was cleared
  // but the unified-auth HttpOnly cookie is still valid).
  //
  // Post FIND-H-08 cutover, the unified JWT lives ONLY in the HttpOnly
  // `treatx_auth` cookie — JS cannot read it (see
  // `utils/unifiedAuth.js::getUnifiedAuth`). The readable equivalent
  // is the `treatx_unified_user` snapshot in localStorage. Its
  // presence is a strong hint that a valid cookie is likely still
  // present (login-side writes them atomically, logout-side clears
  // them atomically). The protected route's own permission gate
  // (below) still keeps real access decisions canonical, so this only
  // smooths the hydration race.
  const hasUnifiedToken = () => {
    try {
      const snapshot = localStorage.getItem('treatx_unified_user');
      if (!snapshot) return false;
      const parsed = JSON.parse(snapshot);
      return !!(parsed && (parsed.sub || parsed.email || parsed.role));
    } catch { return false; }
  };

  // OF-001E §C2 — workspace-existence admission.
  //
  // Pull the union of role_ids the user can legitimately access from
  // EITHER the RBAC context (preferred) OR localStorage (when RBAC
  // hasn't hydrated yet on a hard refresh).  This list is stable
  // across workspace switches and does NOT depend on localStorage
  // `treatx_last_active_workspace` (which races every transition).
  const accessibleRoles = (() => {
    const fromCtx = (workspaces || [])
      .filter(w => w && w.is_available)
      .map(w => w.role_id);
    if (fromCtx.length) return fromCtx;
    try {
      const stored = JSON.parse(localStorage.getItem('treatx_rbac_workspaces') || '[]');
      return (Array.isArray(stored) ? stored : [])
        .filter(w => w && w.is_available)
        .map(w => w.role_id);
    } catch { return []; }
  })();

  const isClinicUser = () => {
    try {
      const storedPerms = localStorage.getItem('treatx_rbac_permissions');
      const perms = storedPerms ? JSON.parse(storedPerms) : [];
      return perms.some(p => p.startsWith('clinic.') || p.startsWith('lead.') || p.startsWith('offer.'));
    } catch { return false; }
  };

  const authValid = isAuthenticated || hasStoredPerms() || hasUnifiedToken();
  if (!authValid) return <Navigate to="/login" replace />;

  // TREATX-UAT-PROD-ADMISSION-RACE-01 (Codex solution spec, 2026-09-28).
  //
  // `contextRequired === false` used to mean BOTH "the server confirmed no
  // selection is needed" AND "the server has not answered yet", so a hard
  // reload could admit protected children on the CACHED snapshot before the
  // initial authoritative `GET /auth/unified/me` reconciliation landed. The
  // children then mounted, fired scope-bearing requests and painted the
  // 403s as data (the original production L0), and a stale/revoked cached
  // workspace looked admissible for one commit.
  //
  // `profileLoaded` is the existing readiness signal and is now
  // authoritative: it is true ONLY after a complete authoritative payload
  // (login hydration, /me, or a validated context POST) has been applied,
  // and it drops back to false synchronously with every canonical context
  // invalidation. While it is false we render a NEUTRAL bootstrap state —
  // no children, no child effects, no protected requests, no workspace
  // inference, no cached-workspace redirect and no premature selector.
  // This runs before role, permission, contextRequired and ReconsentGate
  // handling, so permission-only routes (TreatX Admin, Accounting) are
  // covered too — and it never requires a Provider workspace.
  if (!profileLoaded) {
    return <AuthorizationBootstrap />;
  }

  // TREATX-UAT-PROD-CONTEXT-SURFACE-01 (production UAT, 2026-09-28).
  //
  // ROOT CAUSE of the production batch (symptoms A/B/C/E-provider/G):
  // admission here is deliberately computed from the workspace CATALOGUE
  // (what the account may access), never from the ACTIVE context. When
  // the server reports that no active workspace context is selected
  // (`context_state = selection_required` ⇒ `contextRequired`), every
  // scope-bearing API call fail-closes with 403 CONTEXT_REQUIRED
  // ("Select a workspace to continue."), yet the route still rendered
  // its full surface. The screens then presented those failures as
  // DATA — "My Treatments (0)", no subscription crown, zero Lead Funnel,
  // "Could not load operational summary", a bare access-denied toast —
  // and the user was stranded with no recovery until a write surfaced
  // the server message.
  //
  // Only 8 of ~35 provider routes were additionally wrapped in
  // `WorkspaceBootstrapGuard`, so the recovery surface was unreachable
  // from the rest. Fixed at the ONE shared admission boundary and
  // delegated to the EXISTING recovery surface — no per-route patching,
  // no new authority, and no relaxation of the server-authoritative
  // rule (authenticated User + selected Provider + selected Role).
  //
  // The route's own canonical `allowedRoles` declaration is forwarded so
  // the recovery surface can tell whether a single-workspace account is
  // an UNAMBIGUOUS auto-selection for THIS surface. No route→workspace
  // mapping table is introduced (TREATX-UAT-PROD-CONTEXT-SURFACE-01b).
  if (contextRequired) {
    return <WorkspaceBootstrapGuard allowedRoles={allowedRoles}>{children}</WorkspaceBootstrapGuard>;
  }

  // OF-001E §C2 — admit if any `allowedRoles` value is in the user's
  // accessibleRoles (their workspace catalogue).  This is the correct
  // SSOT: what workspaces the user has, not which one is currently
  // active.
  if (allowedRoles && allowedRoles.length > 0) {
    const isAdmin = accessibleRoles.some(r =>
      r === 'treatx_super_admin' || r === 'treatx_admin'
    );
    const allowed = isAdmin || allowedRoles.some(r => accessibleRoles.includes(r));
    if (!allowed) {
      // User genuinely does not have access to this route.  Bounce
      // them to their active workspace's default route — NOT to
      // `/clinic/home`, which would trigger the redirect cascade.
      if (activeWorkspace?.default_route) {
        return <Navigate to={activeWorkspace.default_route} replace />;
      }
      return <Navigate to="/" replace />;
    }
    return (
      <>{children}</>
    );
  }

  if (permission) {
    // OF-001E §C2 — permission check uses workspace catalogue's perms,
    // not transient context state.  We honour permission only as a
    // secondary gate (after allowedRoles); if the user has no
    // permission, send them to their active workspace, not /clinic/home.
    let hasIt = false;
    // TREATX-PRODUCTION-P0-OWNER-ACCEPTANCE-02 §P0-7:
    // The previous code denied access when `treatx_rbac_permissions`
    // didn't include the required key. For super-admins on a fresh
    // hard-refresh, the perms key is sometimes hydrated *after*
    // the first render — and we'd bounce a fully-legitimate
    // owner to their workspace (or the wrong place). Treat
    // super-admin in role_assignments as a stable ADMISSION SHORTCUT
    // independent of the (still loading) perms array.
    let isSuperAdminByAssignment = false;
    try {
      const storedPerms = localStorage.getItem('treatx_rbac_permissions');
      const perms = storedPerms ? JSON.parse(storedPerms) : [];
      hasIt = perms.includes(permission);
    } catch { /* hasIt stays false */ }
    try {
      const storedRoles = localStorage.getItem('treatx_rbac_role_assignments');
      const roles = storedRoles ? JSON.parse(storedRoles) : [];
      isSuperAdminByAssignment = Array.isArray(roles) && roles.some(
        r => r?.role_id === 'treatx_super_admin' || r?.role_id === 'treatx_admin'
      );
    } catch { /* stays false */ }
    if (!hasIt && !isSuperAdminByAssignment) {
      if (activeWorkspace?.default_route) {
        return <Navigate to={activeWorkspace.default_route} replace />;
      }
      return <Navigate to="/" replace />;
    }
  }
  return (
    <>{children}</>
  );
};

// WAVE 2.1 P0-4: Approval Access Gate
// Clinic users MUST have status=approved (i.e., have a clinic_id) to access internal routes.
// OF-001C: multi-role users (e.g. patient who accepted provider_sales_rep)
// authenticate as `organization_type: 'patient'` but legitimately switch
// into a provider workspace. The gate now also accepts when the ACTIVE
// WORKSPACE carries a clinic_id — that workspace was already validated by
// the canonical /me writer.
const ClinicApprovedRoute = ({ children }) => {
  try {
    const activeClinicId = getActiveWorkspaceClinicId();
    if (activeClinicId) return children; // active provider workspace bound to clinic
    const storedUser = localStorage.getItem('treatx_unified_user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.organization_type === 'clinic' && !user.clinic_id) {
        return <Navigate to="/clinic/application-status" replace />;
      }
    }
  } catch { /* proceed */ }
  return children;
};

// OF-001F #2 — Smart redirect: `/clinic/home` → the ACTIVE WORKSPACE's
// canonical dashboard.  OWNER VALIDATION FAILURE 2 demanded that
// `/clinic/home` never be an active intermediate during workspace
// transitions.  We keep the route declaration (legacy bookmarks) but
// resolve the redirect from the SAME LOCALSTORAGE SSOT that
// `switchWorkspace` writes to FIRST during a switch.  That means if
// `/clinic/home` is ever reached (e.g. via stale bookmark) it ALWAYS
// resolves to the user's most recently chosen workspace, never a
// stale React-state snapshot.
//
// Reading from localStorage (not `useRBAC().activeWorkspace`) closes
// the last race where React state could lag behind a same-tick
// `switchWorkspace` write.
const ClinicRoleRedirect = () => {
  const { isAuthenticated } = useRBAC();
  if (!isAuthenticated) {
    // Defer to stored auth token before sending to /login — avoids
    // double-bounce on cold reload at /#/clinic/home.
    const hasToken = typeof window !== 'undefined' && !!null;
    if (!hasToken) return <Navigate to="/login" replace />;
  }
  // Read SSOT from localStorage (set atomically by switchWorkspace
  // BEFORE any React re-render or URL mutation).
  let target = null;
  try {
    const list = JSON.parse(localStorage.getItem('treatx_rbac_workspaces') || '[]') || [];
    const activeId = localStorage.getItem('treatx_last_active_workspace');
    if (activeId) {
      const match = list.find((w) => w && w.workspace_id === activeId);
      if (match && match.is_available !== false && match.default_route) {
        target = match.default_route;
      }
    }
    if (!target) {
      const first = list.find((w) => w && w.is_available && w.default_route);
      if (first) target = first.default_route;
    }
  } catch { /* ignore parse errors */ }
  if (target) return <Navigate to={target} replace />;
  return <Navigate to="/" replace />;
};

/**
 * TREATX-SEARCH-V1-FINAL-RETIRE-01 (2026-02) — Redirect helper to canonical
 * /search-v2. Search V1 has been permanently retired; every legacy entry
 * point (`/search`, `/find-clinics`, `/patient/clinic-search`) flows here
 * and is redirected to `/search-v2` while preserving the full query string
 * so deep links carrying `treatment`, `country`, `package`, etc. survive intact.
 */
const SearchRedirect = () => {
  const loc = useLocation();
  return <Navigate to={{ pathname: '/search-v2', search: loc.search }} replace />;
};

function AppContent() {
  const { loading } = useAuth();
  const location = useLocation();

  // WAVE 2.1 P1-6: Global confetti cleanup on route changes
  useEffect(() => {
    const cleanup = () => {
      document.querySelectorAll('canvas').forEach(c => {
        if (c.style.position === 'fixed' && c.style.pointerEvents === 'none') {
          c.remove();
        }
      });
    };
    window.addEventListener('hashchange', cleanup);
    return () => window.removeEventListener('hashchange', cleanup);
  }, []);

  // TREATX-GOOGLE-OAUTH-FRONTEND-01: Emergent OAuth callback intercept removed.
  // Google login now uses direct GIS id_token exchange — no redirect, no
  // session_id in hash. Any leftover `session_id=` hash from cached
  // Stripe/Emergent flows is handled by the more specific success/cancel
  // payment routes.

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // TREATX-CLINIC-MESSAGES-UX-CORRECTION-10D + SEARCH-V2-WORKSPACE-POLISH-01:
  // workspace surfaces are application screens, not marketing pages. The
  // global site Footer must NOT be visible and the page must occupy the
  // full viewport so internal panels (composers, sticky detail panels)
  // anchor cleanly without exposing the marketing footer.
  const isWorkspaceRoute = (
    location.pathname.startsWith('/clinic/messages')
    || location.pathname.startsWith('/patient/messages')
    || location.pathname.startsWith('/search-v2')
  );

  // PROVIDER-ONBOARDING-SCROLL-FIX-01: Provider register/onboarding are LONG
  // scrolling forms, not locked dashboards. They must scroll natively (no
  // h-screen / overflow-hidden — that clips the bottom CTA at 100% zoom and
  // breaks the sticky action bar) but STILL hide the marketing Footer.
  const isNoFooterScrollRoute = (
    location.pathname.startsWith('/provider/register')
    || location.pathname.startsWith('/provider/onboarding')
  );

  // PUBLIC-SEO-LAUNCH-GAP-REMEDIATION (2026-02) — non-indexable surfaces.
  // Anything that is a private workspace, search results, admin console,
  // authentication surface, checkout return, or workflow tool MUST NOT
  // appear in search engine indexes. `robots.txt` gives crawlers a hint
  // (Disallow), but Disallow alone does NOT drop an already-indexed URL —
  // for that Google needs a page-level `noindex`. This block mounts a
  // global <SEO noindex> whenever the current hash path matches a
  // non-public prefix. Public marketing pages continue to render their
  // own per-page <SEO> with `index, follow`. Because HashRouter is used
  // here, `location.pathname` is the in-app path (React Router's hash
  // parser normalises it), not the shell path.
  const NON_PUBLIC_PREFIXES = [
    '/treatx-admin',
    '/clinic/', '/provider/', '/patient/', '/salesrep/', '/doctor/',
    '/coordinator/', '/after-sales/', '/onboarding/', '/offers/',
    '/auth/', '/oauth/', '/admin/',
    '/search', '/search-v2', '/find-clinics', '/saved-searches',
    '/my-tasks',
    '/invite/', '/payment-success', '/payment-cancel',
    '/forgot-password', '/reset-password', '/settings/',
    '/login', '/portal', '/build', '/parity', '/system-status',
    '/patient/consent',
  ];
  const isNonPublicRoute = NON_PUBLIC_PREFIXES.some((p) =>
    p.endsWith('/') ? location.pathname.startsWith(p) : (location.pathname === p || location.pathname.startsWith(p + '/'))
  );

  return (
    <div className={`App ${isWorkspaceRoute ? 'h-screen overflow-hidden' : 'min-h-screen'} flex flex-col`}>
      {isNonPublicRoute ? (
        <SEO title="TreatX" description="TreatX private workspace." noindex />
      ) : null}
      <Header />
      {/* OF-001D — Phase C: keep active workspace in lockstep with the URL. */}
      <WorkspaceRouteSync />
      <PatientFlowProgress />
      {/* OF-001 — global pending-invitation surface; self-hides when empty. */}
      <PendingInvitationsBanner />
      {/* CONSENT-RECONSENT-INTERCEPTOR-01 — re-consent modal stack at app shell.
          Self-hides when feature-flagged off or no pending Class-A consents. */}
      <GlobalConsentInterceptor />
      <PatientIdentityCompletionInterceptor />
      <VersionBadge />
      <main className={`flex-1 ${isWorkspaceRoute ? 'pt-20 overflow-hidden' : 'pt-20'}`}>
        <Routes>
          {/* Public Routes */}
          {/* TREATX-LANDING-V3-PHASE-A — `/` resolves to the version published
              server-side (default V1). `/v1`, `/v2`, `/v3` are preview routes and
              never change publication state. */}
          <Route path="/" element={<HomepageVersionGate />} />
          <Route path="/v1" element={<LandingPage />} />
          <Route path="/v2" element={<LandingPageV2 />} />
          <Route path="/v3" element={<LandingPageV3 />} />
          <Route path="/build" element={<BuildInfo />} />
          {/* DEPLOY-TRUTH-R1 — public, no-auth parity certainty surface.
              The owner opens `/#/parity` to see whether their browser is
              looking at the same FE/BE deployment lineage that Emergent
              validated.  Green banner = synchronized.  Red banner =
              stale browser / partial deploy. */}
          <Route path="/parity" element={<ParityCheckPage />} />
          <Route path="/parity-check" element={<Navigate to="/parity" replace />} />
          <Route path="/deployment-truth" element={<Navigate to="/parity" replace />} />
          <Route path="/login" element={<UnifiedLoginPage />} />  {/* Sprint 1: Unified Login */}
          <Route path="/mfa/enroll" element={<MfaEnrollPage />} />
          <Route path="/mfa/challenge" element={<MfaChallengePage />} />
          {/* TREATX-ADMIN-ONBOARDING-01 · frontend routes */}
          <Route path="/admin/onboarding/change-password" element={<AdminOnboardingChangePassword />} />
          <Route path="/admin/onboarding/verify-required" element={<AdminOnboardingVerifyRequired />} />
          <Route path="/admin/onboarding/verify-email" element={<AdminOnboardingVerifyEmail />} />
          {/* TREATX-APPLE-MOBILE-CALLBACK-BLANK-SCREEN-01 — Apple redirect landing */}
          <Route path="/auth/apple/callback" element={<AppleCallback />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />  {/* TREATX-AUTH-PASSWORD-RESET-SELFSERVE-01 */}
          {/* PRD1.9 Phase 1: /login-legacy, /portal removed — unified login only */}
          <Route path="/login-legacy" element={<Navigate to="/login" replace />} />
          <Route path="/portal" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/treatments" element={<TreatmentsPage />} />
          <Route path="/treatments/:slug" element={<TreatmentDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/reactivate" element={<ReactivateAccountPage />} />  {/* TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 */}
          <Route path="/cookie-settings" element={<CookieSettingsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/health-tourism-info" element={<HealthTourismInfoPage />} />
          <Route path="/open-source" element={<OpenSourceLicensesPage />} />
          <Route path="/legal/:contentKey" element={<LegalDocumentPage />} />  {/* LEGAL-PUBLIC-FOOTER-PROCESS-01 generic canonical legal doc resolver */}
          <Route path="/patient/settings/legal" element={<PatientLegalSettings />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/system-status" element={<SystemStatus />} />
          {/* PRD1.9 Phase 1: /admin removed — TreatX admin is at /treatx-admin */}
          <Route path="/admin" element={<Navigate to="/treatx-admin/dashboard" replace />} />
          
          {/* Patient Auth Routes */}
          <Route path="/patient/register" element={<PatientRegisterPage />} />

          {/* Patient Routes */}
          <Route
            path="/patient/onboarding"
            element={<PatientOnboarding />}
          />
          <Route
            path="/patient/intake-enhanced"
            element={<PatientIntakeEnhanced />}
          />
          <Route
            path="/patient/clinic-search"
            element={<SearchRedirect />}
          />
          {/* Public Clinic Profile (Phase 1.1) */}
          <Route path="/clinics/:clinicId" element={<PublicClinicProfile />} />
          {/* Find Me Clinics — P6 foundation: redirect to canonical /search preserving query */}
          <Route path="/find-clinics" element={<SearchRedirect />} />
          {/* Saved Searches (Patient) */}
          <Route path="/saved-searches" element={<SavedSearchesPage />} />
          {/* SEARCH-V2-RETIRE-V1 (2026-02): canonical patient search is /search-v2.
              /search now redirects (preserving query string) to keep every
              legacy bookmark, partner deep-link, and internal navigation
              working without surfacing the retired V1 page. */}
          <Route path="/search" element={<SearchRedirect />} />
          <Route path="/search-v2" element={<SearchV2Page />} />
          <Route
            path="/patient/request-confirmation"
            element={<ClinicRequestConfirmation />}
          />
          <Route
            path="/patient/offers"
            element={<MyOffers />}
          />
          <Route
            path="/offers/compare/:leadId"
            element={<OfferComparisonPage />}
          />
          <Route
            path="/patient/payment"
            element={<PaymentPage />}
          />
          <Route
            path="/patient/payment-success"
            element={<PaymentSuccessPage />}
          />
          <Route
            path="/patient/payment-failed"
            element={<PaymentFailedPage />}
          />
          <Route
            path="/patient/payment-history"
            element={<PaymentHistory />}
          />
          <Route
            path="/patient/documents"
            element={<PatientDocuments />}
          />
          <Route
            path="/patient/booking-timeline"
            element={<BookingTimeline />}
          />
          {/* Alias for timeline */}
          <Route
            path="/patient/timeline"
            element={<Navigate to="/patient/booking-timeline" replace />}
          />
          <Route
            path="/patient/travel-details"
            element={<TravelDetails />}
          />
          {/* Alias for travel */}
          <Route
            path="/patient/travel"
            element={<Navigate to="/patient/travel-details" replace />}
          />
          <Route
            path="/patient/messages"
            element={<PatientMessagesV2 />}
          />
          <Route
            path="/patient/medical-files"
            element={<MedicalFilesPage />}
          />
          {/* Patient Dashboard - P3-1: Using PatientDashboard with UX improvements */}
          <Route
            path="/patient/dashboard"
            element={<PatientDashboard />}
          />
          {/* P7Z-3: Canonical landing for patient quote-request results */}
          <Route
            path="/patient/requests"
            element={<PatientDashboard />}
          />
          <Route path="/patient/quotes/:quoteId" element={<PatientQuoteView />} />
          <Route path="/patient/orders/:orderId" element={<PatientOrderDetail />} />
          <Route path="/patient/settings/notifications" element={<PatientNotificationSettings />} />
          {/* TREATX-11Y-PATIENT-DASHBOARD-HOOKS-01 — canonical patient feed */}
          <Route path="/patient/notifications" element={<PatientNotificationsCenter />} />
          <Route path="/patient/journey" element={<PatientJourney />} />
          {/* M16B-03 — Clinical Profile editor */}
          <Route path="/patient/clinical-profile/edit" element={<ClinicalProfileEdit />} />

          {/* Clinic Routes */}
          <Route path="/clinic/login" element={<Navigate to="/login" replace />} /> {/* Redirect to unified login */}
          {/* PRD1.9 Phase 1: /clinic/login-legacy removed — redirect to unified login */}
          <Route path="/clinic/login-legacy" element={<Navigate to="/login" replace />} />
          {/* PRD1.9 SSOT: /clinic/register hard-redirects to canonical /provider/register */}
          <Route path="/clinic/register" element={<Navigate to="/provider/register?type=clinic" replace />} />
          {/* /clinic/pending-approval route removed — use /provider/register + /#/login instead */}
          <Route path="/clinic/application-status" element={<ApplicationStatusCheckPage />} /> {/* Phase 1: Check status */}
          <Route path="/clinic/rejection-status" element={<RejectionStatusPage />} /> {/* Batch 1.5: Rejection details & Reapply */}
          {/* /clinic/approved route removed — no longer part of provider onboarding */}
          <Route path="/clinic/trial-success" element={<TrialActivationSuccessPage />} /> {/* Phase 1 Batch 1.4: Trial success */}
          <Route path="/clinic/upgrade-success" element={<UpgradeSuccessPage />} /> {/* Phase 1 Batch 1.4: Upgrade success */}
          {/* Smart /clinic/home redirect — routes each role to correct dashboard */}
          <Route path="/clinic/home" element={<ClinicRoleRedirect />} />
          <Route path="/clinic/admin" element={<RBACProtectedRoute permission="clinic.manage" allowedRoles={['provider_admin']}><ClinicApprovedRoute><WorkspaceBootstrapGuard><ClinicAdminDashboard /></WorkspaceBootstrapGuard></ClinicApprovedRoute></RBACProtectedRoute>} />
          <Route path="/clinic/admin/dashboard" element={<RBACProtectedRoute permission="clinic.manage" allowedRoles={['provider_admin']}><ClinicApprovedRoute><WorkspaceBootstrapGuard><ClinicAdminDashboard /></WorkspaceBootstrapGuard></ClinicApprovedRoute></RBACProtectedRoute>} />
          <Route path="/clinic/admin/leads/:leadId" element={<RBACProtectedRoute permission="lead.view" allowedRoles={['provider_admin', 'provider_coordinator']}><ClinicAdminDashboard /></RBACProtectedRoute>} />
          <Route path="/clinic/admin/users" element={<RBACProtectedRoute permission="user.view" allowedRoles={['provider_admin']}><UserManagement /></RBACProtectedRoute>} />
          {/* TREATX-PROVIDER-CMS-CONTRACTS-01 — provider-owned contracts
              (canonical `legal_documents`). Restricted to provider_admin
              with `clinic.manage`. Cross-tenant writes are gated server-side
              by `_assert_document_write_scope` in document_routes.py. */}
          <Route path="/clinic/admin/contracts" element={<RBACProtectedRoute permission="clinic.manage" allowedRoles={['provider_admin']}><ClinicApprovedRoute><ContractsManagement /></ClinicApprovedRoute></RBACProtectedRoute>} />
          {/* COORDINATOR-WORKSPACE-001: canonical operational workspace.
              Shared by provider_admin (supervisory) and provider_coordinator
              (primary operator). RBAC = case.view (both roles carry it). */}
          <Route path="/clinic/operations" element={<RBACProtectedRoute permission="case.view" allowedRoles={['provider_admin', 'provider_coordinator']}><ClinicApprovedRoute><WorkspaceBootstrapGuard><ClinicOperationsDashboard /></WorkspaceBootstrapGuard></ClinicApprovedRoute></RBACProtectedRoute>} />
          <Route path="/clinic/sales" element={<RBACProtectedRoute permission="lead.view" allowedRoles={['provider_sales_rep']}><ClinicApprovedRoute><WorkspaceBootstrapGuard><SalesRepDashboard /></WorkspaceBootstrapGuard></ClinicApprovedRoute></RBACProtectedRoute>} />
          {/* PRD1.9 Phase 1: /clinic/doctor removed — doctors use /doctor/* routes */}
          <Route path="/clinic/doctor" element={<Navigate to="/doctor/profile" replace />} />
          <Route path="/clinic/aftercare" element={<RBACProtectedRoute permission="aftercare.view"><AfterSalesDashboard /></RBACProtectedRoute>} />
          <Route path="/clinic/after-sales/dashboard" element={<RBACProtectedRoute permission="aftercare.view" allowedRoles={['provider_after_sales_rep']}><WorkspaceBootstrapGuard><AfterSalesDashboard /></WorkspaceBootstrapGuard></RBACProtectedRoute>} />
          {/* COA-01 T4: legacy /clinic/milestone-templates now silently
              redirects to canonical /clinic/treatment-plans. Bookmarks
              and old deep-links still resolve; users only ever see the
              canonical URL and the "Treatment Plans" label. */}
          <Route path="/clinic/milestone-templates" element={<Navigate to="/clinic/treatment-plans" replace />} />
          {/* OWNER-E2E-VISUAL-AUDIT-REMEDIATION-001 F1+F3 (2026-05-31)
              Canonical product name is "Treatment Plans". */}
          <Route path="/clinic/treatment-plans" element={<RBACProtectedRoute permission="clinic.view"><MilestoneTemplatesPage /></RBACProtectedRoute>} />
          <Route path="/clinic/admin/requirements" element={<RBACProtectedRoute permission="clinic.view" allowedRoles={['provider_admin']}><RequirementCatalog /></RBACProtectedRoute>} />
          {/* FE-WIRING-1B: clinic/coordinator canonical operational view for a case */}
          <Route path="/clinic/cases/:caseId/workflow" element={<RBACProtectedRoute permission="clinic.view"><ClinicCaseWorkflow /></RBACProtectedRoute>} />
          {/* M16B-07D-B1 — Coordinator Journey Planner: spreadsheet planner
              wired to patient_case_plans (canonical post-payment workflow). */}
          <Route path="/clinic/cases/:caseId/journey-planner" element={<RBACProtectedRoute permission="clinic.view" allowedRoles={['provider_admin', 'provider_coordinator']}><CoordinatorJourneyPlanner /></RBACProtectedRoute>} />
          {/* WORKFLOW-OPS-001-PHASE-1 (P0-B) — clinic operational case list.
              Shared by provider_admin and provider_coordinator; tenant scope
              is enforced by the underlying GET /api/cases endpoint. */}
          <Route path="/clinic/cases" element={<RBACProtectedRoute permission="case.view" allowedRoles={['provider_admin', 'provider_coordinator']}><ClinicCasesList /></RBACProtectedRoute>} />
          {/* WORKFLOW-OPS-001-PHASE-2A (P0-C) — canonical My Tasks surface
              for every provider role. Patient is excluded by the global
              header (no "My Tasks" link rendered for patient role) and
              by the in-page clinic-scope guard (patient has no clinic_id).
              The doctor workspace is sometimes marked is_available=false
              when affiliation isn't yet bound to an approved clinic;
              we deliberately do not gate by `allowedRoles` so that the
              doctor still reaches the page and sees their assigned-tasks
              feed scoped by the backend RBAC. */}
          <Route path="/my-tasks" element={<RBACProtectedRoute><MyTasksPage /></RBACProtectedRoute>} />
          <Route path="/coordinator/cases/:caseId/workflow" element={<RBACProtectedRoute permission="clinic.view"><ClinicCaseWorkflow /></RBACProtectedRoute>} />
          {/* TCPN PHASE 10-B2B B2D: before/after cases with consent enforcement */}
          <Route path="/clinic/before-after-cases" element={<RBACProtectedRoute permission="clinic.view"><BeforeAfterCasesPage /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/before-after-moderation" element={<RBACProtectedRoute permission="content.manage"><BeforeAfterModerationQueue /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/soft-launch-invites" element={<RBACProtectedRoute permission="content.manage"><SoftLaunchInviteKeysPage /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/launch-control" element={<RBACProtectedRoute permission="content.manage"><LaunchControlPage /></RBACProtectedRoute>} />  {/* TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 */}
          {/* PAYOUT-UI-01: canonical payout/withdrawal admin workflow (SEC-002).
              Route uses `permission` alone. The RBAC gate's permission
              branch admits BOTH super_admin (via role_assignments
              bypass) AND provider users whose cached permissions
              include `payout.view` (e.g. provider_accountant). Adding
              `allowedRoles` would break admin because admin's
              workspaces catalogue is [] and the allowedRoles branch
              denies on empty accessibleRoles. */}
          {/* UI-A: /treatx-admin/payouts + /clinic/accountant/payouts routes retired — normal withdrawal-request model deleted (F6D + F6C). */}
          {/* ESCROW-UI-01: extends the Accountant Workspace with an escrow tab. */}
          <Route path="/treatx-admin/escrow" element={<RBACProtectedRoute permission="finance.view"><EscrowPanel /></RBACProtectedRoute>} />
          {/* UI-C · Admin cross-provider finance monitoring + recovery visibility. */}
          <Route path="/treatx-admin/finance" element={<RBACProtectedRoute permission="finance.view"><AdminFinance /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/corporate-identity" element={<RBACProtectedRoute permission="entity.manage"><CorporateIdentityAdmin /></RBACProtectedRoute>} />
          <Route path="/clinic/accountant/escrow" element={<RBACProtectedRoute permission="finance.view"><EscrowPanel /></RBACProtectedRoute>} />

          {/* TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN — anonymous
              verification-link landing page. Reachable from any device
              and gate-allowlisted at both the ASGI middleware and the
              client-side SoftLaunchGate. Never redirects to Login. */}
          <Route path="/onboarding/verify-email" element={<VerifyEmailLandingPage />} />
          {/* TCPN PHASE S1-C1 — clinic logo moderation + clinic-side upload */}
          <Route path="/treatx-admin/logo-moderation" element={<RBACProtectedRoute permission="content.manage"><LogoModerationQueue /></RBACProtectedRoute>} />
          <Route path="/clinic/admin/logo" element={<RBACProtectedRoute permission="clinic.manage" allowedRoles={['provider_admin']}><ClinicApprovedRoute><ClinicLogoPage /></ClinicApprovedRoute></RBACProtectedRoute>} />
          {/* OF-006 — facility-media (clinic_profile context) moderation + upload */}
          <Route path="/treatx-admin/facility-media-moderation" element={<RBACProtectedRoute permission="content.manage"><FacilityMediaModerationQueue /></RBACProtectedRoute>} />
          <Route path="/clinic/admin/facility-media" element={<RBACProtectedRoute permission="clinic.manage" allowedRoles={['provider_admin']}><ClinicApprovedRoute><ClinicFacilityMediaPage /></ClinicApprovedRoute></RBACProtectedRoute>} />
          {/* TCPN PHASE 10-B2B B2D.6: patient self-consent UX (any authenticated user can record/withdraw) */}
          <Route path="/patient/consent/before-after" element={<BeforeAfterConsentPage />} />
          {/* TREATX-L1E: canonical patient deep-link path used by the
              consent-request notification email (see /send). Kept in
              sync with backend `send_request_to_patient`. */}
          <Route path="/before-after/consent-request/:token" element={<BeforeAfterConsentPage />} />
          <Route path="/before-after/consent" element={<BeforeAfterConsentPage />} />
          {/* Sprint 10: Clinic-facing package selection / upgrade page */}
          {/* TCPN PHASE 11A.4: /clinic/packages and /clinic/upgrade are duplicates of
              /clinic/subscription's "Plans" tab. We redirect rather than delete to
              preserve every legacy deep-link (trial expiry, usage limits, feature
              gates, payment cancel/success, etc.) without surfacing two competing
              UX flows. /clinic/select-plan is intentionally preserved for the
              post-approval onboarding path where the clinic has no subscription yet. */}
          <Route path="/clinic/packages" element={<Navigate to="/clinic/subscription?tab=plans" replace />} />
          <Route path="/clinic/upgrade" element={<Navigate to="/clinic/subscription?tab=plans" replace />} />
          <Route path="/clinic/select-plan" element={<PackageSelectionPage />} /> {/* Phase 1: Post-approval */}
          {/* P3 Phase 3: Clinic subscription payment pages */}
          <Route path="/clinic/payment-success" element={<ClinicPaymentSuccess />} />
          <Route path="/clinic/payment-cancel" element={<ClinicPaymentCancel />} />
          {/* Subscription Management Interface */}
          <Route path="/clinic/subscription" element={<SubscriptionManagementPage />} />
          <Route path="/clinic/upgrade-success" element={<ClinicPaymentSuccess />} /> {/* Upgrade success redirects here */}
          
          {/* Auto-Release Settings (Phase 5.1) */}
          <Route path="/clinic/settings/auto-release" element={<RBACProtectedRoute permission="clinic.manage"><ClinicAutoReleaseSettings /></RBACProtectedRoute>} />
          <Route path="/clinic/settings/notifications" element={<RBACProtectedRoute permission="clinic.view"><ClinicNotificationSettings /></RBACProtectedRoute>} />
          {/* Clinic Profile Editor (Phase 1.1) */}
          <Route path="/clinic/profile" element={<RBACProtectedRoute permission="clinic.manage"><ClinicApprovedRoute><ClinicProfileEditor /></ClinicApprovedRoute></RBACProtectedRoute>} />
          {/* Trust & Verification Submission (PRD1.7 Step 4B) */}
          <Route path="/clinic/trust" element={<RBACProtectedRoute permission="clinic.manage"><ClinicTrustSubmission /></RBACProtectedRoute>} />
          {/* Campaign Management (Phase 1.1) */}
          <Route path="/clinic/campaigns" element={<RBACProtectedRoute permission="clinic.manage"><ClinicCampaignsDashboard /></RBACProtectedRoute>} />
          <Route path="/clinic/campaigns/performance" element={<RBACProtectedRoute permission="clinic.manage"><ClinicCampaignPerformance /></RBACProtectedRoute>} />
          <Route path="/clinic/notifications" element={<RBACProtectedRoute permission="clinic.view"><ClinicNotificationsCenter /></RBACProtectedRoute>} />
          {/* PROVIDER-FUNCTIONAL-CORRECTNESS-PACK: parallel `/clinic/campaigns/create`
              route retired. The canonical surface is `/clinic/campaigns` (dashboard). */}
          <Route path="/clinic/campaigns/create" element={<Navigate to="/clinic/campaigns" replace />} />
          
          {/* Clinic Representative Routes */}
          <Route path="/clinic/representative/login" element={<Navigate to="/login" replace />} /> {/* Redirect to unified */}
          <Route path="/clinic/representative" element={<RBACProtectedRoute permission="clinic.view"><ClinicRepresentativeDashboard /></RBACProtectedRoute>} />
          <Route path="/clinic/representative/dashboard" element={<RBACProtectedRoute permission="clinic.view"><ClinicRepresentativeDashboard /></RBACProtectedRoute>} />
          
          {/* Clinic Messaging V2 */}
          <Route path="/clinic/messages" element={<RBACProtectedRoute permission="messaging.view"><ClinicApprovedRoute><ClinicMessagesV2 /></ClinicApprovedRoute></RBACProtectedRoute>} />
          <Route path="/clinic/quote-requests" element={<RBACProtectedRoute permission="offer.view"><ClinicQuoteRequests /></RBACProtectedRoute>} />
          {/* P4: Clinic Self-Service Scorecard Dashboard */}
          <Route path="/clinic/analytics/scorecards" element={<RBACProtectedRoute permission="reporting.view"><ClinicScorecardDashboard /></RBACProtectedRoute>} />
          {/* Offer Templates */}
          <Route path="/clinic/offer-templates" element={<RBACProtectedRoute permission="offer.view"><OfferTemplatesPage /></RBACProtectedRoute>} />
          
          <Route
            path="/clinic/setup"
            element={
              <ProtectedRoute requiredType="clinic">
                <ClinicSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clinic/dashboard"
            element={
              <ProtectedRoute requiredType="clinic">
                <ClinicDashboard />
              </ProtectedRoute>
            }
          />

          {/* M11-CLOSURE-001 (2026-05-31) — Orphan removal.
              Per M11-INBOX-CONSISTENCY-AUDIT-001 finding #6, the entire
              /agent/* surface (AgentDashboard, LeadDetail, OrderDetail,
              QuoteBuilder, AgentLogin) consumed /api/crm/* endpoints
              that do not exist in this backend — every counter and CRUD
              operation returned 0/404. Routes are now redirected to the
              unified /login. The component files remain on disk for
              audit purposes but are unreachable from the router. */}
          <Route path="/agent/login"                          element={<Navigate to="/login" replace />} />
          <Route path="/agent/leads"                          element={<Navigate to="/login" replace />} />
          <Route path="/agent/leads/:leadId"                  element={<Navigate to="/login" replace />} />
          <Route path="/agent/leads/:leadId/quote/create"     element={<Navigate to="/login" replace />} />
          <Route path="/agent/orders/:orderId"                element={<Navigate to="/login" replace />} />

          {/* TreatX Coordinator Routes */}
          <Route path="/coordinator/login" element={<Navigate to="/login" replace />} /> {/* Redirect to unified */}
          {/* PRD1.9 Phase 1: /coordinator/login-legacy removed — redirect to unified login */}
          <Route path="/coordinator/login-legacy" element={<Navigate to="/login" replace />} />
          {/* POST-CLEANUP: The TreatX-side /coordinator/dashboard portal has been
              physically removed.  Provider-coordinator role lands on
              /clinic/operations (see SECURITY-S1 + workspace cleanup sprints).
              Any deep-link to the old URL falls through to the 404 catch-all. */}

          {/* TreatX Admin Routes */}
          <Route path="/treatx-admin/login" element={<Navigate to="/login" replace />} /> {/* Redirect to unified */}
          {/* PRD1.9 Phase 1: /treatx-admin/login-legacy removed — redirect to unified login */}
          <Route path="/treatx-admin/login-legacy" element={<Navigate to="/login" replace />} />
          <Route path="/treatx-admin/dashboard" element={<RBACProtectedRoute permission="content.manage"><TreatXAdminDashboard /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/treatments" element={<RBACProtectedRoute permission="content.manage"><TreatmentManagement /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/categories" element={<RBACProtectedRoute permission="content.manage"><CategoryManagement /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/providers" element={<RBACProtectedRoute permission="content.manage"><ProviderValidationPanel /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/provider-domain" element={<RBACProtectedRoute permission="content.manage"><AdminProviderDomain /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/messaging" element={<RBACProtectedRoute permission="content.manage"><AdminMessagingPanel /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/analytics" element={<RBACProtectedRoute permission="content.manage"><AdminAnalyticsDashboard /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/verification-center" element={<RBACProtectedRoute permission="content.manage"><ClinicVerificationCenter /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/trust-score" element={<RBACProtectedRoute permission="content.manage"><AdminTrustScoreDashboard /></RBACProtectedRoute>} />
          {/* Phase 5.1: Auto-Release Admin */}
          <Route path="/treatx-admin/auto-release" element={<RBACProtectedRoute permission="content.manage"><AdminAutoReleasePage /></RBACProtectedRoute>} />
          {/* Phase 1.1: Profile Review */}
          <Route path="/treatx-admin/profile-review/:clinicId?" element={<RBACProtectedRoute permission="content.manage"><AdminProfileReview /></RBACProtectedRoute>} />
          {/* PACKAGE-ADMIN-HYGIENE-1: canonical merged package admin surface.
              Single product editor (Catalog), gated diagnostics, and
              gated cleanup inventory live inside `PackagesAdminMerged`. */}
          <Route 
            path="/treatx-admin/packages" 
            element={
              <RBACProtectedRoute permission="content.manage">
                <PackagesAdminMerged />
              </RBACProtectedRoute>
            } 
          />
          {/* PACKAGE-ADMIN-HYGIENE-1: `/treatx-admin/provider-packages` was the
              legacy alias mounting the duplicate package-definition editor.
              The duplicate editor has been physically excised; this route is
              kept ONLY as a one-shot redirect to the canonical Catalog tab
              so any pre-existing bookmarks degrade gracefully. */}
          <Route 
            path="/treatx-admin/provider-packages" 
            element={<Navigate to="/treatx-admin/packages?tab=catalog" replace />} 
          />
          {/* Redirect base admin route to dashboard for consistency */}
          <Route path="/treatx-admin" element={<Navigate to="/treatx-admin/dashboard" replace />} />
          {/* Contact Inquiries Management */}
          <Route path="/treatx-admin/inquiries" element={<RBACProtectedRoute permission="content.manage"><TreatXAdminInquiries /></RBACProtectedRoute>} />
          {/* Clinic Applications Management - Phase 1 Batch 1.3 */}
          {/* /treatx-admin/clinic-applications removed — superseded by /treatx-admin/provider-domain */}
          {/* P1: Email Templates Management */}
          <Route path="/treatx-admin/email-templates" element={<RBACProtectedRoute permission="content.manage"><TreatXAdminEmailTemplates /></RBACProtectedRoute>} />
          {/* TREATX-COMMERCIAL-GATING-ADMIN-HARDENING-09B */}
          <Route path="/treatx-admin/commercial-gating" element={<RBACProtectedRoute permission="content.manage"><CommercialGatingPolicy /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/product-languages" element={<RBACProtectedRoute permission="content.manage"><ProductLanguagesPolicy /></RBACProtectedRoute>} />
          {/* USER-ROLE-MGMT-UI-01 · manage role_assignments[] on existing canonical rbac_users */}
          <Route path="/treatx-admin/user-roles" element={<RBACProtectedRoute permission="user.manage"><UserRoleManagement /></RBACProtectedRoute>} />
          {/* TREATX-NOTIFICATION-ADMIN-OPS-11B — additive admin views over canonical services */}
          <Route path="/treatx-admin/notification-ops" element={<RBACProtectedRoute permission="reporting.view"><NotificationOpsDashboard /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/notification-ops/log" element={<RBACProtectedRoute permission="reporting.view"><NotificationsLogViewer /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/notification-ops/consent" element={<RBACProtectedRoute permission="reporting.view"><AdminConsentDashboard /></RBACProtectedRoute>} />
          {/* TREATX-AGREEMENT-MANAGEMENT-ADMIN-UI-01 — Admin Agreement Library */}
          <Route path="/treatx-admin/agreements" element={<RBACProtectedRoute permission="content.manage"><AdminAgreements /></RBACProtectedRoute>} />
          {/* TREATX Self-Service Legal Admin — new document / usage / policies */}
          <Route path="/treatx-admin/legal-documents/new" element={<RBACProtectedRoute permission="content.manage"><NewLegalDocumentPage /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/legal-documents/usage" element={<RBACProtectedRoute permission="content.manage"><DocumentUsagePage /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/legal-policies" element={<RBACProtectedRoute permission="content.manage"><LegalPoliciesPage /></RBACProtectedRoute>} />
          {/* TREATX-LEGAL-MULTILINGUAL-ADMIN-01 — per-locale legal body editor */}
          <Route path="/treatx-admin/legal-multilingual" element={<RBACProtectedRoute permission="content.manage"><LegalMultilingualEditor /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/notification-ops/templates-status" element={<RBACProtectedRoute permission="reporting.view"><EmailTemplateOperations /></RBACProtectedRoute>} />
          {/* TREATX-11D-WHATSAPP-ADMIN-UI-01 — WhatsApp admin (UI over canonical endpoints) */}
          <Route path="/treatx-admin/notification-ops/whatsapp" element={<RBACProtectedRoute permission="messaging.view"><WhatsAppAdmin /></RBACProtectedRoute>} />
          {/* TREATX-WA-DIAGNOSTIC-TEST-01 — temporary super-admin-only WhatsApp test (removable after UAT).
              Uses permission="content.manage" (same guard as every other /treatx-admin route, which honours the
              super-admin role_assignments shortcut). Strict treatx_super_admin enforcement is on the BACKEND
              endpoint (POST /api/admin/diagnostics/whatsapp/send-test → 403 SUPER_ADMIN_REQUIRED). */}
          <Route path="/treatx-admin/whatsapp-test" element={<RBACProtectedRoute permission="content.manage"><WhatsAppTest /></RBACProtectedRoute>} />
          {/* TREATX-ADMIN-MAINTENANCE-01 — Super-Admin-only (in-page guard + server-side 403) */}
          <Route path="/treatx-admin/maintenance" element={<RBACProtectedRoute permission="content.manage"><MaintenancePage /></RBACProtectedRoute>} />
          {/* TREATX-MANAGED-RUNTIME-CONTENT-01 — Super-Admin-only (in-page guard + server-side 403) */}
          <Route path="/treatx-admin/content-management" element={<RBACProtectedRoute permission="content.manage"><ContentManagement /></RBACProtectedRoute>} />
          {/* TREATX-09C-OPS-VISIBILITY-DASHBOARD-01 — Content Protection telemetry view */}
          <Route path="/treatx-admin/notification-ops/content-protection" element={<RBACProtectedRoute permission="messaging.view"><ContentProtectionOps /></RBACProtectedRoute>} />
          {/* PRD1.8 Phase 3: Doctor Verification Admin */}
          <Route path="/treatx-admin/doctor-verification" element={<RBACProtectedRoute permission="content.manage"><AdminDoctorVerification /></RBACProtectedRoute>} />
          {/* P1.2: Patient Email Templates */}
          <Route path="/treatx-admin/patient-email-templates" element={<RBACProtectedRoute permission="content.manage"><PatientEmailTemplates /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/positioning-tags" element={<RBACProtectedRoute permission="content.manage"><AdminPositioningTags /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/vip-services" element={<RBACProtectedRoute permission="content.manage"><AdminVipServices /></RBACProtectedRoute>} />
          {/* TREATX-WEBSITE-MGMT-01 — ONE entry point for the public website.
              A thin task-oriented shell that renders the four EXISTING
              management surfaces in place; each still talks to its own
              canonical authority. No fifth CMS, no content copied.

              The four legacy routes below are KEPT so existing deep links,
              bookmarks and docs still work, and each one now lands on the
              matching consolidated tab instead of a stranded page. */}
          <Route path="/treatx-admin/website-management" element={<RBACProtectedRoute permission="content.manage"><WebsiteManagement /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/landing-stats" element={<Navigate to="/treatx-admin/website-management?tab=homepage" replace />} />
          <Route path="/treatx-admin/landing-cms" element={<Navigate to="/treatx-admin/website-management?tab=homepage" replace />} />
          <Route path="/treatx-admin/provider-benefits-cms" element={<Navigate to="/treatx-admin/website-management?tab=provider-benefits" replace />} />
          <Route path="/treatx-admin/homepage-version" element={<Navigate to="/treatx-admin/website-management?tab=preview-publish" replace />} />
          <Route path="/treatx-admin/v3-draft-preview" element={<RBACProtectedRoute permission="content.manage"><V3DraftReview /></RBACProtectedRoute>} />

          <Route path="/treatx-admin/translations" element={<RBACProtectedRoute permission="content.manage"><AdminTranslationStatus /></RBACProtectedRoute>} />
          {/* V2 is ARCHIVED. Kept reachable by its existing direct route only —
              deliberately NOT surfaced in Website Management navigation. */}
          <Route path="/treatx-admin/landing-sections" element={<RBACProtectedRoute permission="content.manage"><AdminLandingSections /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/sponsored-placements" element={<RBACProtectedRoute permission="content.manage"><AdminSponsoredPlacements /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/campaign-review" element={<RBACProtectedRoute permission="content.manage"><AdminCampaignReview /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/exposure-rules" element={<RBACProtectedRoute permission="content.manage"><AdminExposureRules /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/commission-rules" element={<RBACProtectedRoute permission="content.manage"><AdminCommissionRules /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/treatment-taxonomy" element={<RBACProtectedRoute permission="content.manage"><AdminTreatmentTaxonomy /></RBACProtectedRoute>} />
          <Route path="/treatx-admin/ranking-policy" element={<RBACProtectedRoute permission="content.manage"><AdminRankingPolicy /></RBACProtectedRoute>} />
          {/* /treatx-admin/document-rules route removed — unified into /treatx-admin/provider-domain */}
          
          {/* Provider Onboarding Routes */}
          <Route path="/provider/register" element={<ProviderOnboarding />} />
          <Route path="/provider/onboarding" element={<ProviderOnboarding />} />
          {/* PROVIDER-BENEFITS-LANDING-PAGE-01: public CMS marketing page */}
          <Route path="/provider-benefits" element={<ProviderBenefits />} />
          {/* TREATX-CHANGE-PASSWORD-01: canonical self-service password change for ALL roles */}
          <Route path="/settings/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

          {/* K1: Team Invitation Acceptance Route */}
          <Route path="/register" element={<InviteAcceptPage />} />
          <Route path="/invite/accept" element={<InviteAcceptPage />} />

          {/* Accounting Dashboard — read-only financial reporting */}
          <Route path="/accounting/dashboard" element={<RBACProtectedRoute permission="accounting.view"><AccountingDashboard /></RBACProtectedRoute>} />
          <Route path="/clinic/accounting/dashboard" element={<RBACProtectedRoute permission="accounting.view" allowedRoles={['provider_accountant']}><WorkspaceBootstrapGuard><AccountantWorkspace /></WorkspaceBootstrapGuard></RBACProtectedRoute>} />
          {/* ACCOUNTANT-WORKSPACE-001 — dense grid-first JWT-scoped accountant workspace.
              Visible to provider_accountant (primary) + provider_admin (read-only supervisory).
              RBAC = reporting.view; role allow-list enforced server-side via _enforce_accountant_roles. */}
          <Route path="/clinic/accountant/workspace" element={<RBACProtectedRoute permission="accounting.view" allowedRoles={['provider_accountant', 'provider_admin']}><WorkspaceBootstrapGuard><AccountantWorkspace /></WorkspaceBootstrapGuard></RBACProtectedRoute>} />

          {/* PRD1.8: Doctor Identity & Profile (SSOT) */}
          <Route path="/doctor/signup" element={<DoctorSignup />} />
          <Route path="/doctor/login" element={<DoctorLogin />} />
          <Route path="/doctor/profile" element={<DoctorProfile />} />
          <Route path="/doctor/affiliations" element={<DoctorAffiliations />} />
          {/* DOCTOR-WORKSPACE-001 — dense, grid-first operational workspace.
              Visible to provider_doctor (primary) + provider_admin (read-only
              supervisory). RBAC = case.view (both roles carry it). */}
          <Route path="/doctor/workspace" element={<RBACProtectedRoute permission="case.view" allowedRoles={['provider_doctor', 'provider_admin']}><DoctorWorkspace /></RBACProtectedRoute>} />

          {/* AFTER-SALES-WORKSPACE-001 — dense, grid-first recovery workspace.
              Visible to provider_after_sales_rep (primary) + provider_admin
              (read-only supervisory). RBAC = case.view (both roles carry it). */}
          <Route path="/after-sales/workspace" element={<RBACProtectedRoute permission="case.view" allowedRoles={['provider_after_sales_rep', 'provider_admin']}><AfterSalesWorkspace /></RBACProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<UnknownRoute />} />
        </Routes>
      </main>
      {/* TREATX-CLINIC-MESSAGES-UX-CORRECTION-10D: messaging surfaces
          are workspace apps — the marketing footer must not appear
          here. Hide the Footer on `/clinic/messages` and
          `/patient/messages`. */}
      {!isWorkspaceRoute && !isNoFooterScrollRoute && <Footer />}
      <Toaster />
      <CookieConsentBanner />
    </div>
  );
}

/**
 * CrossSurfaceBoundary — TREATX-SEO-RECOVERY-S2
 *
 * Mounted only on the public (BrowserRouter) surface. That surface is allowed
 * to render public pages; the moment a visitor navigates to anything else —
 * Login, provider registration, a dashboard — the hash application has to take
 * over, because that is where session handling, the post-login redirects and
 * every payment/OAuth return live.
 *
 * `replace` (not `assign`) keeps the back button pointing at the public page
 * the visitor came from, and because the destination is a hash URL that the
 * legacy normaliser never rewrites, the handover cannot loop.
 */
const CrossSurfaceBoundary = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (isPublicPath(pathname)) return;
    // TREATX-SEO-RECOVERY-S3A — only a KNOWN private destination is handed
    // over. An unknown path stays here and renders the 404 page; bouncing it
    // into the hash app is what used to turn it into a homepage at HTTP 200.
    if (!isPrivatePath(pathname)) return;
    // The query string travels with the navigation (e.g. ?redirect=…); a
    // document fragment is intentionally dropped — it would collide with the
    // hash route itself.
    window.location.replace(`${window.location.origin}/#${pathname}${search || ''}`);
  }, [pathname, search]);
  return null;
};

/**
 * UnknownRoute — TREATX-SEO-RECOVERY-S3A
 *
 * The catch-all used to be `<Navigate to="/" replace />`, which answered every
 * unknown URL with the homepage at HTTP 200. On the PUBLIC surface that is a
 * soft-404 factory, so it now renders a real Not Found page (noindex +
 * prerender-status-code 404).
 *
 * The PRIVATE hash surface keeps the old bounce-to-homepage behaviour: the
 * authenticated app relies on it (stale deep links, post-logout redirects), it
 * is never crawled, and changing it would be a UX regression no crawler asked
 * for.
 */
const UnknownRoute = () => {
  if (getActiveSurface() !== 'public') return <Navigate to="/" replace />;
  return <NotFoundPage />;
};

function App() {
  // TREATX-SEO-RECOVERY-S2 — one router per document load, chosen from the
  // address the visitor arrived on and then FROZEN for the lifetime of the
  // mount (`useState` initialiser, not a re-computed value). Public pages get
  // `BrowserRouter` so they live at real, crawlable URLs; everything else keeps
  // the `HashRouter` the whole private application is built on. The same route
  // table below is rendered either way — there is no second tree.
  const [surface] = React.useState(() => {
    const resolved = resolveSurface();
    // Remembered so components inside the shared route table (the 404 route)
    // can tell which surface they are rendering on.
    setActiveSurface(resolved);
    return resolved;
  });
  const Router = surface === 'public' ? BrowserRouter : HashRouter;

  // TREATX-GOOGLE-OAUTH-FRONTEND-01: read Client ID from env (fail-fast if missing).
  const tree = (
    <HelmetProvider>
    <I18nextProvider i18n={i18n}>
      <Router>
        {/* Public surface only: leaving the public pages means leaving the
            public router, so hand the navigation to the hash application with
            one full document load. */}
        {surface === 'public' ? <CrossSurfaceBoundary /> : null}
        {/* TREATX-L1F: canonical surface (TR vs GLOBAL) resolver.
            Sits outside every domain provider so any component can
            call useSurface() / useSurfaceRule() without a re-render. */}
        <SurfaceProvider>
        <RBACProvider>
        <AuthProvider>
          <AgentAuthProvider>
            <CoordinatorAuthProvider>
              <CoordinatorDataProvider>
                <ClinicRepresentativeAuthProvider>
                  <ClinicRepresentativeDataProvider>
                    <TreatXAdminAuthProvider>
                      <CategoryManagementProvider>
                        <TreatmentManagementProvider>
                            <ProviderOnboardingProvider>
                              <PackageEnforcementProvider>
                                <SuspensionEnforcementProvider>
                                <PackageCatalogProvider>
                                <OrganizationRolesProvider>
                                  <BoardCertificationProvider>
                                    <RatingsProvider>
                              <PaymentProvider>
                                <ClinicDataProvider>
                                  <MonetizationProvider>
                                  <PatientFlowProvider>
                                    <DocumentProvider>
                                      {/* R6-IMPL-002: TimelineProvider removed — zero live consumers. */}
                                        <TravelProvider>
                                          <MessagingProvider>
                                            <HandoffNotificationProvider>
                                              <ContactInquiryProvider>
                                              <LanguageProvider>
                                                <SoftLaunchGate>
                                                  <AppContent />
                                                  <HandoffNotificationContainer />
                                                  <GlobalHandoffConfirmationModal />
                                                </SoftLaunchGate>
                                              </LanguageProvider>
                                              </ContactInquiryProvider>
                                            </HandoffNotificationProvider>
                                          </MessagingProvider>
                                        </TravelProvider>
                                      {/* end R6-IMPL-002 TimelineProvider removal */}
                                    </DocumentProvider>
                                  </PatientFlowProvider>
                                  </MonetizationProvider>
                                </ClinicDataProvider>
                              </PaymentProvider>
                            </RatingsProvider>
                          </BoardCertificationProvider>
                        </OrganizationRolesProvider>
                                </PackageCatalogProvider>
                                </SuspensionEnforcementProvider>
                                </PackageEnforcementProvider>
                      </ProviderOnboardingProvider>
                  </TreatmentManagementProvider>
                </CategoryManagementProvider>
              </TreatXAdminAuthProvider>
                  </ClinicRepresentativeDataProvider>
                </ClinicRepresentativeAuthProvider>
              </CoordinatorDataProvider>
            </CoordinatorAuthProvider>
          </AgentAuthProvider>
        </AuthProvider>
        </RBACProvider>
        </SurfaceProvider>
        <EnvBadge />
      </Router>
    </I18nextProvider>
    </HelmetProvider>
  );
  // If a Google Client ID is configured, wrap the tree so any descendant
  // can render the official GoogleLogin button. If missing, render the
  // tree as-is — the Google button will hide itself and email/password
  // login continues to work.
  // TREATX-GOOGLE-GIS-LOAD-ON-SURFACE-01 — Google Identity Services is NO
  // LONGER loaded globally at app start. The provider is mounted only on the
  // surfaces that actually render a Google sign-in button (UnifiedLoginPage),
  // so visitors who never reach a login surface do not load Google's script.
  // One Tap and automatic sign-in remain disabled at the button call sites.
  return tree;
}

export default App;
